import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { FormInput, FormSelect } from '../finance';
import { X, CircleNotch } from '@phosphor-icons/react';
import staffService from '../../services/staffService';
import './transport-forms.css';

function DriverForm({ driver, onSave, onClose }) {
  const [formData, setFormData] = useState(driver || {
    staff_id: '',
    license_number: '',
    license_expiry: '',
    role: 'driver',
    insurance_provider: '',
    insurance_policy_number: '',
    insurance_expiry: '',
    is_active: true,
    is_verified: false,
    notes: ''
  });
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadStaff = async () => {
      try {
        const response = await staffService.listStaff({ limit: 100 });
        const staffData = Array.isArray(response) ? response : response.items || response.staff || [];
        setStaff(staffData);
      } catch (error) {
        console.error('Failed to load staff', error);
        setStaff([]);
      }
    };
    loadStaff();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.staff_id || !formData.license_number) {
      toast.error('Staff and license number are required');
      return;
    }

    setLoading(true);
    try {
      await onSave(driver?.id, formData);
    } catch (error) {
      if (error.response?.status === 409 || error.message?.includes('duplicate')) {
        toast.error('This staff member is already registered as a driver');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to save driver');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2>{driver ? 'Edit Driver' : 'Register Driver'}</h2>
          <button onClick={onClose} className="btn-close"><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-row">
            <FormSelect
              label="Staff Member"
              name="staff_id"
              value={formData.staff_id}
              onChange={handleChange}
              required
              options={staff
                .filter(s => s.staff_type === 'non_teaching')
                .map(s => ({
                  value: s.id,
                  label: `${s.first_name} ${s.last_name} (${s.staff_id})`
                }))}
              placeholder="-- Select Staff --"
            />

            <FormSelect
              label="Role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              options={[
                { value: 'driver', label: 'Driver' },
                { value: 'conductor', label: 'Conductor' }
              ]}
            />
          </div>

          <div className="form-row">
            <FormInput
              label="License Number"
              name="license_number"
              value={formData.license_number}
              onChange={handleChange}
              required
              placeholder="e.g., DL-2023-001"
            />
            <FormInput
              label="License Expiry Date"
              name="license_expiry"
              type="date"
              value={formData.license_expiry}
              onChange={handleChange}
            />
          </div>

          <div className="form-row">
            <FormInput
              label="Insurance Provider"
              name="insurance_provider"
              value={formData.insurance_provider}
              onChange={handleChange}
            />
            <FormInput
              label="Insurance Policy Number"
              name="insurance_policy_number"
              value={formData.insurance_policy_number}
              onChange={handleChange}
            />
          </div>

          <FormInput
            label="Insurance Expiry Date"
            name="insurance_expiry"
            type="date"
            value={formData.insurance_expiry}
            onChange={handleChange}
          />

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_verified"
                checked={formData.is_verified}
                onChange={handleChange}
              />
              Verified
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
              />
              Active
            </label>
          </div>

          <FormInput
            label="Notes"
            name="notes"
            as="textarea"
            value={formData.notes}
            onChange={handleChange}
            rows="3"
          />

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (
                <span className="button-loading">
                  <CircleNotch size={16} weight="fill" className="spinner" />
                  Saving...
                </span>
              ) : driver ? 'Update Driver' : 'Register Driver'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DriverForm;
